import { type Session } from "@supabase/supabase-js";
import { createContext, useContext } from "react";
import type { AdminApi, SamsAccess } from "@/api";
import type { AdminPromptState, AgentState, SkillCatalog } from "@/types";

/**
 * Two contexts, each for a different reason.
 *
 * `SessionContext` exists because `AdminShell` became a route component and
 * can no longer take props. `App` still owns the session — Supabase hands it a
 * new object on every token refresh — so passing it down a context keeps those
 * refreshes from touching the router. Recreating the router to carry the
 * session instead would reset navigation state every refresh.
 *
 * `AdminShellContext` carries what the shell already owned before routing:
 * the shared banner/spinner state and the three datasets it fetches. Routes
 * read it and hand the same props to the same page components, so the pages
 * themselves are unchanged.
 *
 * `apiBaseUrl` is editable in the sidebar, which is why none of this lives in
 * the router's own context — that one is resolved when the router is built,
 * and this value changes while it runs.
 */

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession must be used inside SessionContext");
  return session;
}

export type AdminShellValue = {
  session: Session;
  api: AdminApi;
  apiBaseUrl: string;

  /** Shared across pages: the sidebar reload button and the two banners. */
  loading: boolean;
  saving: boolean;
  setSaving: (value: boolean) => void;
  showError: (err: unknown) => void;
  flash: (message: string) => void;
  /**
   * Bumped by the header's Reload on screens that fetch their own data
   * (All Test Cases · Batch Test · Test Result). They re-read when it moves.
   */
  reloadKey: number;

  /**
   * The login's SAMS access (`/admin/sams/admins/me`). It decides whether the
   * Admins screen is offered; SAMS answers it for an active admin only, so
   * anything else (its 403, a failure) is "denied" with the reason shown.
   */
  samsAccess: SamsAccessState;
  /**
   * Forget the cached `/me` and ask again, keeping the current state until the
   * answer arrives (after an admin change, so buttons and the gate follow it).
   */
  refreshSamsAccess: () => Promise<void>;

  // --- Agents ---
  // The picker moved out of the sidebar, which is navigation only now, and
  // into the Agents screen. The selection still lives here because the shell
  // seeds it when the catalogue first loads.
  agents: AgentState[];
  setAgents: React.Dispatch<React.SetStateAction<AgentState[]>>;
  skillCatalog: SkillCatalog | null;
  selectedId: string;
  setSelectedId: (id: string) => void;
  selectedAgent: AgentState | undefined;

  // --- Prompt Lab ---
  promptState: AdminPromptState | null;
  setPromptState: (state: AdminPromptState | null) => void;
  loadPromptLab: () => Promise<void>;

  // --- Negative feedback ---
};

export type SamsAccessState =
  | { state: "loading" }
  | { state: "admin"; me: SamsAccess }
  | { state: "denied"; reason: string; code?: string };

/**
 * What a signed-in account that is not a SAMS admin sees (SAMS 403 `FORBIDDEN`)
 * instead of the server's message, which names its user id. An admin finds the
 * account in the Admins screen's sign-up list.
 */
export const NON_ADMIN_GUIDANCE = "관리자에게 추가를 요청하세요. 관리자 화면의 가입자 목록에 이 계정이 보입니다.";

/** SAMS's 403 for a valid login that is not an active admin. */
export function isNotAdminError(err: unknown) {
  return typeof err === "object" && err !== null && (err as { status?: unknown }).status === 403
    && (err as { code?: unknown }).code === "FORBIDDEN";
}

export const AdminShellContext = createContext<AdminShellValue | null>(null);

export function useAdminShell(): AdminShellValue {
  const value = useContext(AdminShellContext);
  if (!value) throw new Error("useAdminShell must be used inside AdminShell");
  return value;
}
